import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import PageHero from 'tio-ui/components/page-hero';
import FilterTabs from 'tio-ui/components/filter-tabs';
import MatchScoreBadge from 'tio-ui/components/match-score-badge';
import SkillChip from 'tio-ui/components/skill-chip';
import TaEligibilityBadge from 'tio-ui/components/ta-eligibility-badge';
import StatusBadge from 'tio-ui/components/status-badge';
import Timeline from 'tio-ui/components/timeline';
import {
  PhArrowLeft,
  PhCheckCircle,
  PhClock,
  PhCode,
  PhGraduationCap,
  PhGridFour,
  PhInfo,
  PhListDashes,
  PhSparkle,
  PhTag,
  PhTarget,
} from 'ember-phosphor-icons';

const SAMPLE_TABS = [
  { id: 'all', label: 'All', count: 24 },
  { id: 'recommended', label: 'Recommended', count: 8 },
  { id: 'in-progress', label: 'In Progress', count: 3 },
  { id: 'completed', label: 'Completed', count: 5 },
];

const SAMPLE_SKILLS = [
  { name: 'Leadership', level: 85, category: 'Management' },
  { name: 'Patient Care', level: 95, category: 'Clinical' },
  { name: 'Epic EMR', level: 72, category: 'Technical' },
  { name: 'Budget Management', level: 45, category: 'Management' },
];

export default class PatternsDetailPage extends Component {
  @tracked activeTab = 'all';

  setActiveTab = (tabId: string) => {
    this.activeTab = tabId;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      <div class="mb-4">
        <LinkTo @route="authenticated.reimagined.components" class="tio-demo-link">
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      <PageHero
        @title="Patterns"
        @subtitle="FilterTabs, MatchScoreBadge, SkillChip, TaEligibilityBadge, StatusBadge, and Timeline."
        @icon={{PhCode}}
        @gradient="brand"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">6 Components</Chip>
        </:actions>
      </PageHero>

      <div class="space-y-8">

        {{! ── FilterTabs ── }}
        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhGridFour @size={{20}} class="tio-fg-brand" />
              <span>FilterTabs</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Horizontal pill-style filter buttons with active state and optional count badges.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4">
              <FilterTabs
                @tabs={{SAMPLE_TABS}}
                @activeId={{this.activeTab}}
                @onSelect={{this.setActiveTab}}
              />
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>const tabs = [ { id: 'all', label: 'All',
                  count: 24 }, { id: 'active', label: 'Active', count: 8 }, ]; &lt;FilterTabs
                  @tabs=&#123;&#123;tabs&#125;&#125;
                  @activeId=&#123;&#123;this.activeTab&#125;&#125;
                  @onSelect=&#123;&#123;this.setActiveTab&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ── MatchScoreBadge ── }}
        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhTarget @size={{20}} class="tio-fg-brand" />
              <span>MatchScoreBadge</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Circular percentage display with automatic color coding based on thresholds.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4">
              <div class="flex items-end gap-8">
                {{#each (array 95 78 55 32) as |score|}}
                  <div class="text-center">
                    <MatchScoreBadge @score={{score}} @size="lg" />
                    <Text @variant="muted" @size="xs" class="mt-2">
                      {{#if (gte score 90)}}Excellent{{else if (gte score 70)}}Good{{else if
                        (gte score 50)
                      }}Fair{{else}}Low{{/if}}
                    </Text>
                  </div>
                {{/each}}
              </div>
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>&lt;MatchScoreBadge
                  @score=&#123;&#123;95&#125;&#125; @size="lg" /&gt; &lt;MatchScoreBadge
                  @score=&#123;&#123;45&#125;&#125; @size="md" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ── SkillChip ── }}
        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhSparkle @size={{20}} class="tio-fg-brand" />
              <span>SkillChip</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Skill tag with optional level indicator bar.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4 space-y-4">
              <div class="tio-wrap">
                {{#each SAMPLE_SKILLS as |skill|}}
                  <SkillChip @name={{skill.name}} @level={{skill.level}} />
                {{/each}}
              </div>
              <div class="pt-4 border-t tio-border-default">
                <Text @size="sm" @variant="muted" class="mb-2">Without level:</Text>
                <div class="tio-wrap-tight">
                  <SkillChip @name="Leadership" @intent="primary" @showLevel={{false}} />
                  <SkillChip @name="Patient Care" @intent="success" @showLevel={{false}} />
                  <SkillChip @name="Epic EMR" @intent="default" @showLevel={{false}} />
                </div>
              </div>
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>&lt;SkillChip @name="Leadership"
                  @level=&#123;&#123;85&#125;&#125; /&gt; &lt;SkillChip @name="Patient Care"
                  @intent="success" @showLevel=&#123;&#123;false&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ── TaEligibilityBadge ── }}
        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhGraduationCap @size={{20}} class="tio-fg-brand" />
              <span>TaEligibilityBadge</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Tuition Assistance eligibility indicator with optional amount and partial coverage.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4">
              <div class="tio-wrap-loose">
                <TaEligibilityBadge @eligible={{true}} @amount="$2,400 covered" />
                <TaEligibilityBadge @eligible={{true}} />
                <TaEligibilityBadge
                  @eligible={{true}}
                  @partial={{true}}
                  @partialAmount="$1,200"
                  @totalAmount="$2,400"
                />
                <TaEligibilityBadge @eligible={{false}} />
              </div>
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>&lt;TaEligibilityBadge
                  @eligible=&#123;&#123;true&#125;&#125; @amount="$2,400 covered" /&gt;
                  &lt;TaEligibilityBadge @eligible=&#123;&#123;true&#125;&#125;
                  @partial=&#123;&#123;true&#125;&#125; @partialAmount="$1,200"
                  @totalAmount="$2,400" /&gt; &lt;TaEligibilityBadge
                  @eligible=&#123;&#123;false&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ── StatusBadge ── }}
        <Section
          class="tio-bg-surface rounded-xl shadow-sm border tio-border-subtle overflow-hidden"
          @headingLevel={{3}}
        >
          <:header>
            <div class="tio-row flex-wrap">
              <div class="w-8 h-8 rounded-lg tio-bg-success-soft tio-row-center">
                <PhTag @size={{18}} class="tio-fg-success" />
              </div>
              StatusBadge
              <Chip @intent="primary" @size="sm">Semantic</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Semantic status chip that auto-maps status strings to intents (completed → success,
              in-progress → primary, planned → default). Wraps tio-ui Chip.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4">
              <div class="tio-wrap">
                <StatusBadge @status="completed" />
                <StatusBadge @status="in-progress" />
                <StatusBadge @status="planned" />
                <StatusBadge @label="Custom Label" @intent="warning" />
                <StatusBadge @label="Error" @intent="danger" />
              </div>
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>&lt;StatusBadge @status="completed" /&gt;
                  &lt;StatusBadge @status="in-progress" /&gt; &lt;StatusBadge @label="Custom"
                  @intent="warning" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ── Timeline ── }}
        <Section
          class="tio-bg-surface rounded-xl shadow-sm border tio-border-subtle overflow-hidden"
          @headingLevel={{3}}
        >
          <:header>
            <div class="tio-row flex-wrap">
              <div class="w-8 h-8 rounded-lg tio-bg-brand-soft tio-row-center">
                <PhListDashes @size={{18}} class="tio-fg-brand" />
              </div>
              Timeline
              <Chip @intent="primary" @size="sm">Layout</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Vertical timeline with named blocks for custom nodes and content. Uses flex layout
              with per-segment connectors that stop at the last item. Supports any item type via
              generics. Provide a
              <code>:node</code>
              block for custom markers or use the default circle.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4 space-y-6">
              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">Default nodes:</Text>
                <div class="max-w-md">
                  <Timeline
                    @items={{array
                      (hash title="Application Submitted" date="Jan 15")
                      (hash title="Under Review" date="Feb 1")
                      (hash title="Approval Pending" date="—")
                    }}
                  >
                    <:content as |item|>
                      <div class="pb-1">
                        <Text @weight="medium" class="tio-fg-primary">{{item.title}}</Text>
                        <Text @size="sm" @variant="muted">{{item.date}}</Text>
                      </div>
                    </:content>
                  </Timeline>
                </div>
              </div>

              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">Colored status
                  nodes:</Text>
                <div class="max-w-md">
                  <Timeline
                    @items={{array
                      (hash title="Application Submitted" date="Jan 15" status="completed")
                      (hash title="Under Review" date="Feb 1" status="in-progress")
                      (hash title="Approval Pending" date="—" status="planned")
                    }}
                  >
                    <:node as |item|>
                      <div
                        class="w-6 h-6 rounded-full
                          {{if
                            (eq item.status 'completed')
                            'tio-bg-success'
                            (if
                              (eq item.status 'in-progress')
                              'tio-bg-info'
                              'tio-demo-timeline-pending'
                            )
                          }}"
                      ></div>
                    </:node>
                    <:content as |item|>
                      <div class="pb-1">
                        <Text @weight="medium" class="tio-fg-primary">{{item.title}}</Text>
                        <Text @size="sm" @variant="muted">{{item.date}}</Text>
                      </div>
                    </:content>
                  </Timeline>
                </div>
              </div>

              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">With icons in
                  nodes:</Text>
                <div class="max-w-md">
                  <Timeline
                    @items={{array
                      (hash title="Application Submitted" date="Jan 15" status="completed")
                      (hash title="Under Review" date="Feb 1" status="in-progress")
                      (hash title="Approval Pending" date="—" status="planned")
                    }}
                  >
                    <:node as |item|>
                      <div
                        class="w-6 h-6 rounded-full tio-row-center
                          {{if
                            (eq item.status 'completed')
                            'tio-bg-success'
                            (if
                              (eq item.status 'in-progress')
                              'tio-bg-info'
                              'tio-demo-timeline-pending'
                            )
                          }}"
                      >
                        {{#if (eq item.status "completed")}}
                          <PhCheckCircle @size={{14}} class="tio-fg-inverse" />
                        {{else if (eq item.status "in-progress")}}
                          <PhClock @size={{14}} class="tio-fg-inverse" />
                        {{else}}
                          <PhInfo @size={{14}} class="tio-fg-inverse" />
                        {{/if}}
                      </div>
                    </:node>
                    <:content as |item|>
                      <div class="pb-1">
                        <Text @weight="medium" class="tio-fg-primary">{{item.title}}</Text>
                        <Text @size="sm" @variant="muted">{{item.date}}</Text>
                      </div>
                    </:content>
                  </Timeline>
                </div>
              </div>
            </div>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>{{! Default nodes (no :node block needed) }}
                  &lt;Timeline @items=&#123;&#123;items&#125;&#125;&gt; &lt;:content as |item|&gt;
                  &lt;Text&gt;&#123;&#123;item.title&#125;&#125;&lt;/Text&gt; &lt;/:content&gt;
                  &lt;/Timeline&gt;

                  {{! Custom colored nodes }}
                  &lt;Timeline @items=&#123;&#123;items&#125;&#125;&gt; &lt;:node as |item|&gt;
                  &lt;div class="w-6 h-6 rounded-full tio-bg-success"&gt;&lt;/div&gt; &lt;/:node&gt;
                  &lt;:content as |item|&gt;...&lt;/:content&gt; &lt;/Timeline&gt;</code></pre>
            </details>
          </:body>
        </Section>
      </div>
    </VStack>
  </template>
}
